/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import { ElementType, useCallback, useMemo } from "react";

import { Box, Stack, Tooltip } from "@mui/material";
import { renderTimeViewClock } from "@mui/x-date-pickers-pro";
import {
  AutocompleteHookForm,
  MultiselectHookForm,
  NumberFieldHookForm,
  SelectHookForm,
  TextFieldHookForm,
  MultiAutocompleteHookForm,
  DayjsLocalizationProvider,
  DatePickerHookForm,
  TimePickerHookForm,
  TimeFieldHookForm,
  ClockIcon,
  DateTimePickerHookForm,
  DateRangePickerHookForm,
  SwitchHookForm,
  RadioHookForm,
  CheckBoxForm,
  TimeRangeFieldHookForm,
  Typography,
  TextFieldMultiLanHookForm,
  SearchLgIcon,
  NumberFieldProps,
  TextFieldProps,
  SelectProps,
  AutocompleteProps,
  DatePickerProps,
  MultiAutocompleteProps,
  FormContainerControlled,
  DataGrid,
  MentionsInputHookForm,
} from "components";
import { gray } from "configs/colors";
import { i18n } from "translations";
import { getPlaceholderSelect, getPlaceholderText } from "utils";

import AutocompleteAsyncHookForm from "./AutocompleteAsyncHookForm";
import CommonButton from "./CommonButton";
import Divider from "./Divider";
import FormGrid from "./FormGrid";
import FormInfoField from "./FormInfoField";
import Heading from "./Heading";
import Upload from "./ImportButton/Upload";
import LoadingOverlay from "./LoadingOverlay";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TFormFieldConfig<T = any> = {
  type:
    | "text"
    | "search"
    | "number"
    | "select"
    | "multi-select"
    | "date-picker"
    | "time-picker"
    | "date-time-picker"
    | "time-field"
    | "time-range"
    | "autocomplete"
    | "multi-autocomplete"
    | "date-range"
    | "autocomplete-async"
    | "switch"
    | "checkbox"
    | "radio"
    | "info"
    | "divider"
    | "heading"
    | "header"
    | "custom"
    | "text-multi-lan"
    | "component"
    | "summary-detail"
    | "button"
    | "upload"
    | "table"
    | "mentions-input";
  name?: keyof T; // Field name on hook form
  col: React.ComponentProps<typeof FormGrid.Item>["desktop"]; // Grid column on desktop
  mobileCol?: React.ComponentProps<typeof FormGrid.Item>["mobile"]; // Grid column on desktop
  label?: string; // Label of field
  labelI18n?: string; // Label of field (i18n)
  placeholder?: string; // Placeholder of field
  placeholderI18n?: string; // Placeholder of field (i18n)
  onlyDesktop?: boolean; // Keep value grid column on mobile screen
  icon?: ElementType;
  content?: string;
  options?: {
    label: string;
    value: any;
    subLabel?: string;
    afterNode?: React.ReactNode;
  }[]; // Option for select, multi-select, radio ...
  tooltipText?: string;
  filterTextPlaceholderI18n?: string; // Placeholder of filter text (i18n)
  required?: boolean;
  disabled?: boolean;
  subLabel?: string;
  subLabelI18n?: string;
  extendProps?: any;
  component?: React.FunctionComponent<any>;
  onExtendProps?: (extend?: any) => any;
  renderCustomNode?: (pos?: any) => React.ReactNode;
  afterNode?: React.ReactNode;
  beforeNode?: React.ReactNode;
  grid?: Partial<React.ComponentProps<typeof FormGrid.Item>["gridProps"]>;
  onHide?: (pos?: any) => boolean;

  // Component props (Form)
  numberFieldProps?: Partial<NumberFieldProps>;
  textFieldProps?: Partial<TextFieldProps>;
  mentionsInputProps?: Partial<
    React.ComponentProps<typeof MentionsInputHookForm>
  >;
  selectProps?: Partial<SelectProps<any>>;
  autocompleteProps?: Partial<AutocompleteProps<any>>;
  multiAutocompleteProps?: Partial<MultiAutocompleteProps<any>>;
  datePickerProps?: Partial<DatePickerProps>;
  autocompleteAsyncProps?: Partial<
    React.ComponentProps<typeof AutocompleteAsyncHookForm>
  >;
  headerProps?: Partial<React.ComponentProps<typeof Typography>>;
  componentProps?: any;
  buttonProps?: Partial<React.ComponentProps<typeof CommonButton>>;
  uploadProps?: Partial<React.ComponentProps<typeof Upload>>;
  tableProps?: Partial<React.ComponentProps<typeof DataGrid>>;
};

export type TFieldArray = {
  name: string;
  fields: any[];
  grid?: Partial<React.ComponentProps<typeof FormGrid.Container>>;
  gridItem?: Partial<React.ComponentProps<typeof FormGrid.Container>>;
};

interface HookFormProps {
  fields: TFormFieldConfig[];
  fieldArray?: TFieldArray;
  grid?: Partial<React.ComponentProps<typeof FormGrid.Container>>;
  loading?: boolean;
  form?: Omit<
    React.ComponentProps<typeof FormContainerControlled>,
    "methods" | "children" | "onSubmit" | "onError"
  > & {
    methods: any;
    onSubmit?: (e?: any) => void;
    onError?: (e?: any) => void;
  };
}

// ---------------------------------------------------------------------------
// Helpers (pure functions – stable references, no re-creation needed)
// ---------------------------------------------------------------------------

/** Set of field types that use "input"-style placeholder */
const TEXT_PLACEHOLDER_TYPES = new Set([
  "text",
  "search",
  "number",
  "text-multi-lan",
]);

/** Set of field types that use "select"-style placeholder */
const SELECT_PLACEHOLDER_TYPES = new Set([
  "select",
  "multi-select",
  "time-picker",
  "autocomplete",
  "multi-autocomplete",
  "date-picker",
  "date-time-picker",
  "time-field",
  "date-range",
  "autocomplete-async",
  "multi-autocomplete-async",
]);

/** Set of field types that need DayjsLocalizationProvider wrapper */
const DATE_TIME_TYPES = new Set([
  "date-picker",
  "time-picker",
  "date-time-picker",
  "time-field",
  "date-range",
  "time-range",
]);

/**
 * Resolve placeholder text from field config.
 * Extracted as a pure function so it can be tested and reused independently.
 */
const resolvePlaceholder = (
  field: TFormFieldConfig,
  label?: string
): string => {
  // Explicit placeholder
  if (field.placeholderI18n) return i18n.t(field.placeholderI18n);
  if (field.placeholder) return field.placeholder;

  // Filter text override
  if (field.filterTextPlaceholderI18n) {
    return `${i18n.t("filterBy")} ${i18n
      .t(field.filterTextPlaceholderI18n)
      .toLocaleLowerCase()}`;
  }

  // Auto-generate from label
  if (label) {
    if (TEXT_PLACEHOLDER_TYPES.has(field.type))
      return getPlaceholderText(label);
    if (SELECT_PLACEHOLDER_TYPES.has(field.type))
      return getPlaceholderSelect(label);
  }

  return "";
};

/**
 * Build the common props shared by most form field renderers.
 */
const buildCommonProps = (
  field: TFormFieldConfig,
  label: string,
  placeholder: string,
  required: boolean,
  disabled: boolean,
  extend?: any
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
) => ({
  label,
  placeholder,
  name: field.name,
  required,
  disabled,
  ...field.extendProps,
  ...(field.onExtendProps && field.onExtendProps(extend)),
});

// ---------------------------------------------------------------------------
// Field Renderer Registry
// ---------------------------------------------------------------------------

type FieldRendererArgs = {
  field: TFormFieldConfig;
  label: string;
  placeholder: string;
  subLabel?: string;
  required: boolean;
  disabled: boolean;
  extend?: any;
};

type FieldRenderer = (args: FieldRendererArgs) => React.ReactNode;

const renderTextMultiLan: FieldRenderer = ({
  field,
  label,
  placeholder,
  required,
  extend,
  disabled,
}) => (
  <TextFieldMultiLanHookForm
    name={field.name}
    label={label}
    required={required}
    textFieldProps={{
      placeholder,
      disabled,
      ...field.extendProps,
      ...(field.onExtendProps && field.onExtendProps(extend)),
      ...field.textFieldProps,
    }}
  />
);

const renderText: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <TextFieldHookForm {...common} {...args.field.textFieldProps} />;
};

const renderSearch: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <TextFieldHookForm
      {...common}
      InputProps={{
        startAdornment: (
          <SearchLgIcon
            sx={{ color: gray[500] }}
            cursor="pointer"
            fontSize="small"
          />
        ),
      }}
      {...args.field.textFieldProps}
    />
  );
};

const renderSelect: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <SelectHookForm
      {...common}
      options={args.field.options || []}
      {...args.field.selectProps}
    />
  );
};

const renderMultiSelect: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <MultiselectHookForm {...common} options={args.field.options || []} />;
};

const renderNumber: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <NumberFieldHookForm {...common} {...args.field.numberFieldProps} />;
};

const renderAutocomplete: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <AutocompleteHookForm
      {...common}
      options={args.field.options || []}
      {...args.field.autocompleteProps}
    />
  );
};

const renderMultiAutocomplete: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <MultiAutocompleteHookForm
      {...common}
      options={args.field.options || []}
      {...args.field.multiAutocompleteProps}
    />
  );
};

const renderAutocompleteAsync: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <AutocompleteAsyncHookForm
      {...common}
      {...args.field.autocompleteAsyncProps}
    />
  );
};

const renderDatePicker: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <DatePickerHookForm {...common} {...args.field.datePickerProps} />;
};

const renderTimePicker: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <TimePickerHookForm
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: renderTimeViewClock,
      }}
      {...common}
    />
  );
};

const renderTimeField: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <TimeFieldHookForm
      format="HH:mm"
      InputProps={{
        startAdornment: (
          <ClockIcon fontSize="small" sx={{ color: gray[700] }} />
        ),
      }}
      {...common}
    />
  );
};

const renderDateTimePicker: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <DateTimePickerHookForm
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: renderTimeViewClock,
      }}
      {...common}
    />
  );
};

const renderDateRange: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <DateRangePickerHookForm {...common} {...args.field.datePickerProps} />
  );
};

const renderTimeRange: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <TimeRangeFieldHookForm {...common} />;
};

const renderSwitch: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <SwitchHookForm {...common} />;
};

const renderRadio: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <RadioHookForm {...common} options={args.field.options || []} />;
};

const renderCheckbox: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return <CheckBoxForm {...common} subTitle={args.subLabel || ""} />;
};

const renderInfo: FieldRenderer = ({ field, label, extend }) => (
  <FormInfoField
    label={label}
    icon={field.icon}
    content={field.content || ""}
    {...field.extendProps}
    {...(field.onExtendProps && field.onExtendProps(extend))}
  />
);

const renderHeader: FieldRenderer = ({ field, label }) => (
  <Typography
    fontWeight={400}
    type="TextSM"
    color="grey.600"
    {...field.extendProps}
    {...field.headerProps}
  >
    {label}
  </Typography>
);

const renderDivider: FieldRenderer = () => <Divider />;

const renderHeading: FieldRenderer = ({
  field,
  label,
  subLabel,
  required,
  extend,
}) => (
  <Heading
    title={label}
    subTitle={subLabel || ""}
    required={required}
    textProps={field.headerProps}
    {...field.extendProps}
    {...(field.onExtendProps && field.onExtendProps(extend))}
  />
);

const renderSummaryDetail: FieldRenderer = ({ label, subLabel }) => (
  <Stack direction="row">
    <Typography fontWeight={400} type="TextSM" color="grey.700">
      {label}
    </Typography>
    :
    <Typography
      fontWeight={700}
      type="TextSM"
      color="grey.700"
      marginLeft="4px"
    >
      {subLabel}
    </Typography>
  </Stack>
);

const renderTable: FieldRenderer = ({ field, extend }) => (
  <DataGrid
    pagination
    paginationMode="server"
    disableRowSelectionOnClick
    initialState={{
      pinnedColumns: { right: ["actions"] },
    }}
    {...field.extendProps}
    {...(field.onExtendProps && field.onExtendProps(extend))}
    {...field.tableProps}
  />
);

const renderCustom: FieldRenderer = ({ field, extend }) =>
  typeof field.renderCustomNode === "function"
    ? field.renderCustomNode(extend)
    : null;

const renderComponent: FieldRenderer = ({ field, extend }) => {
  if (!field.component) return null;
  const Component = field.component;
  return (
    <Component
      {...field.extendProps}
      {...(field.onExtendProps && field.onExtendProps(extend))}
      {...field.componentProps}
    />
  );
};

const renderButton: FieldRenderer = ({ field }) => (
  <CommonButton {...field.buttonProps} />
);

const renderUpload: FieldRenderer = ({ field }) => (
  <Upload {...field.uploadProps} />
);

const renderMentionsInput: FieldRenderer = (args) => {
  const common = buildCommonProps(
    args.field,
    args.label,
    args.placeholder,
    args.required,
    args.disabled,
    args.extend
  );
  return (
    <MentionsInputHookForm {...common} {...args.field.mentionsInputProps} />
  );
};

/**
 * Registry mapping field type → renderer function.
 * Adding a new field type only requires adding one entry here.
 */
const FIELD_RENDERERS: Record<string, FieldRenderer> = {
  "text-multi-lan": renderTextMultiLan,
  text: renderText,
  search: renderSearch,
  select: renderSelect,
  "multi-select": renderMultiSelect,
  number: renderNumber,
  autocomplete: renderAutocomplete,
  "multi-autocomplete": renderMultiAutocomplete,
  "autocomplete-async": renderAutocompleteAsync,
  "date-picker": renderDatePicker,
  "time-picker": renderTimePicker,
  "time-field": renderTimeField,
  "date-time-picker": renderDateTimePicker,
  "date-range": renderDateRange,
  "time-range": renderTimeRange,
  switch: renderSwitch,
  radio: renderRadio,
  checkbox: renderCheckbox,
  info: renderInfo,
  header: renderHeader,
  divider: renderDivider,
  heading: renderHeading,
  "summary-detail": renderSummaryDetail,
  table: renderTable,
  custom: renderCustom,
  component: renderComponent,
  button: renderButton,
  upload: renderUpload,
  "mentions-input": renderMentionsInput,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HookForm: React.FC<HookFormProps> = ({
  grid,
  fields,
  fieldArray,
  loading = false,
  form,
}) => {
  const renderField = useCallback((field: TFormFieldConfig, extend?: any) => {
    const { required = false, disabled = false } = field;
    const label = field.labelI18n ? i18n.t(field.labelI18n) : field.label || "";
    const subLabel = field.subLabelI18n
      ? i18n.t(field.subLabelI18n)
      : field.subLabel;
    const placeholder = resolvePlaceholder(field, label);

    // Hidden field
    if (typeof field.onHide === "function" && field.onHide(extend)) {
      return (
        <Box className={`form-grid-item-hidden-${field.name as string}`} />
      );
    }

    // Dispatch to renderer
    const renderer = FIELD_RENDERERS[field.type];
    if (!renderer) return <div />;

    const node = renderer({
      field,
      label,
      placeholder,
      subLabel,
      required,
      disabled,
      extend,
    });

    // Auto-wrap date/time fields with DayjsLocalizationProvider
    if (DATE_TIME_TYPES.has(field.type)) {
      return <DayjsLocalizationProvider>{node}</DayjsLocalizationProvider>;
    }

    return node;
  }, []);

  const renderFields = useMemo(
    () =>
      fields.map((field, pos) => (
        <FormGrid.Item
          key={pos as never}
          desktop={field.col}
          gridProps={{
            className: `form-grid-item-${field.name as string}`,
            sx: {
              ...field.grid?.sx,
              [`&:has(.form-grid-item-hidden-${field.name as string})`]: {
                display: "none",
              },
            },
            ...(field.grid || {}),
          }}
          {...(field.onlyDesktop && { mobile: field.col })}
          {...(field.mobileCol && { mobile: field.mobileCol })}
        >
          {field.beforeNode}
          {field.tooltipText ? (
            <Tooltip
              title={i18n.t(field.tooltipText)}
              hidden={!field.tooltipText}
              leaveDelay={200}
              placement="top-start"
            >
              <div>{renderField(field)}</div>
            </Tooltip>
          ) : (
            renderField(field)
          )}
          {field.afterNode}
        </FormGrid.Item>
      )),
    [fields, renderField]
  );

  const renderFieldArray = useMemo(() => {
    if (!fieldArray?.name) return <div />;

    return fieldArray.fields.map((fieldContainer, index) => (
      <FormGrid.Item
        desktop={12}
        tablet={12}
        key={fieldContainer?.id}
        gridProps={fieldArray.gridItem?.gridProps}
      >
        <FormGrid.Container {...fieldArray.grid}>
          {fields.map((field, pos) => (
            <FormGrid.Item
              desktop={field.col}
              key={pos as never}
              gridProps={field.grid}
              {...(field.onlyDesktop && { mobile: field.col })}
              {...(field.mobileCol && { mobile: field.mobileCol })}
            >
              {renderField(
                {
                  ...field,
                  name: `${fieldArray.name}.${index}.${
                    (field.name as string) || ""
                  }`,
                },
                {
                  ...fieldContainer,
                  index,
                  containerFieldName: `${fieldArray.name}.${index}`,
                }
              )}
            </FormGrid.Item>
          ))}
        </FormGrid.Container>
      </FormGrid.Item>
    ));
  }, [
    fieldArray?.fields,
    fieldArray?.grid,
    fieldArray?.gridItem,
    fieldArray?.name,
    fields,
    renderField,
  ]);

  const content = (
    <LoadingOverlay loading={loading}>
      <FormGrid.Container {...grid}>
        {fieldArray?.name ? renderFieldArray : renderFields}
      </FormGrid.Container>
    </LoadingOverlay>
  );

  if (form?.methods) {
    return (
      <FormContainerControlled
        // eslint-disable-next-line no-console
        onSubmit={form.onSubmit ? form.onSubmit : console.log}
        // eslint-disable-next-line no-console
        onError={form.onError ? form.onError : console.log}
        {...form}
      >
        {content}
      </FormContainerControlled>
    );
  }

  return content;
};

export default HookForm;
